import React from 'react';
import PropTypes from 'prop-types';
import { ACH_STATUS, } from '../../../modules/Constants';
import SearchBar from '../../searchbar/SearchBar';
import FieldSet from '../../fieldset/FieldSet';
import Grid from '../../grid/Grid';
import TransactionService from '../../../service/TransactionService';
import DetailedTransactionModal from './DetailedTransactionModal';
import ChargebackTransactionForm from '../../form/ChargebackTransactionForm';
import CancelAchForm from '../../form/CancelAchForm';
import TransactionStrategy from './TransactionStrategy';
import HistoryNote from '../../form/HistoryNoteForm';
import RefundForm from '../../form/RefundForm';
import GenResolveForm from '../../form/GenResolveForm';

class TransactionSearchView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
    this.comboFields = [
      {
        value: 'transactionId',
        display: 'Transaction ID',
      },
      {
        value: 'customerId',
        display: 'Customer ID',
      },
      {
        value: 'externalId',
        display: 'External ID',
      },
    ];
    this.v2Factory = TransactionStrategy.getStrategy(TransactionStrategy.V2);
    this.v3Factory = TransactionStrategy.getStrategy(TransactionStrategy.V3);
  }

  componentDidMount() {
    const { requestedState, } = this.props;
    if (requestedState) {
      this.statefulInitialization(requestedState);
    }
  }

  statefulInitialization() {
    const { click, } = this;
    if (click) {
      click();
    }
  }

  onRowSelect(row, version) {
    switch (version) {
      case TransactionStrategy.V2:
        this.setState({
          selectedTransactionV2: row,
        });
        break;
      case TransactionStrategy.V3:
        this.setState({
          selectedTransactionV3: row,
        });
        break;
      case TransactionStrategy.lastFour:
        this.setState({
          selectedTransactionLastFour: row,
        });
        break;
      default:
        throw new Error(`Invalid Version: ${version}`);
    }
  }

  onClick(event) {
    const { combo = {}, search = {}, } = event;
    const { requestedState = {}, } = this.props;
    const { id = '', } = requestedState;

    const criteria = {};
    if (id && !search.value) {
      criteria.customerId = id;
    } else {
      criteria[combo.value] = search.value;
    }
    this.getTransactions(criteria, event.kill);
  }

  getTransactions(criteria, onCompletion) {
    this.reset();

    if (criteria.accountLastFour === undefined) {
      const v2Promise = TransactionService.getTransactionsBySearchCriteria(criteria).then(
        response => response.data
      );

      const v3Promise = TransactionService.getV3TransactionsBySearchCriteria(criteria).then(
        response => response.data
      );

      Promise.all([ v2Promise, v3Promise, ])
        .then(([ v2ResultSet, v3ResultSet, ]) => {
          const v2HeadersAndRows = this.v2Factory.formatRowsAndHeaders(v2ResultSet.transactions);
          const v3HeadersAndRows = this.v3Factory.formatRowsAndHeaders(v3ResultSet.v3transactions);

          this.setState({
            v2: v2HeadersAndRows,
            v3: v3HeadersAndRows,
          });
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          onCompletion();
        });
    } else {
      const lastFourPromise = TransactionService.getTransactionsBySearchCriteriaLastFour(criteria).then(
        response => response.data
      );

      Promise.all([ lastFourPromise, ])
        .then(([ lastFourResultSet, ]) => {
          const lastFourHeadersAndRows = this.v3Factory.formatRowsAndHeaders(lastFourResultSet.lastfourtransactions);

          this.setState({
            lastFour: lastFourHeadersAndRows,
          });
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          onCompletion();
        });
    }
  }

  getOptionalCancelAchHTML(selectedTransaction) {
    if (!selectedTransaction) {
      return null;
    }
    if (selectedTransaction.status !== ACH_STATUS.PENDING) {
      return null;
    }
    return (
      <div className="m-1">
        <CancelAchForm id={selectedTransaction.id} />
      </div>
    );
  }

  getGridStyle() {
    return {
      maxHeight: '400px',
      overflowY: 'auto',
    };
  }

  marginExpandingStyle() {
    return {
      marginTop: '20px',
      marginBottom: '20px',
      marginLeft: '-100px',
      marginRight: '-100px',
    };
  }

  getV2Grid() {
    const { v2 = {}, selectedTransactionV2, } = this.state;
    const { headers = [], rows, } = v2;
    if (headers.length === 0) {
      return null;
    }
    return (
      <div style={this.marginExpandingStyle()}>
        <FieldSet title="V2 Transactions" style={this.getGridStyle()}>
          <Grid
            headers={headers}
            rows={rows}
            onRowSelect={row => this.onRowSelect(row, TransactionStrategy.V2)}
          />
        </FieldSet>
        {this.formatFooter(selectedTransactionV2, TransactionStrategy.V2)}
      </div>
    );
  }

  getV3Grid() {
    const { v3 = {}, selectedTransactionV3, } = this.state;
    const { headers = [], rows = [], } = v3;
    if (headers.length === 0) {
      return null;
    }
    return (
      <div style={this.marginExpandingStyle()}>
        <FieldSet title="V3 Transactions" style={this.getGridStyle()}>
          <Grid
            headers={headers}
            rows={rows}
            onRowSelect={row => this.onRowSelect(row, TransactionStrategy.V3)}
          />
        </FieldSet>
        {this.formatFooter(selectedTransactionV3, TransactionStrategy.V3)}
      </div>
    );
  }

  getLastFourGrid() {
    const { lastFour = {}, selectedTransactionLastFour, } = this.state;
    const { headers = [], rows = [], } = lastFour;
    const filteredRows = rows.filter(row => (row.value[3] !== null || row.value[4] !== null));
    if (headers.length === 0) {
      return null;
    }

    return (
      <div style={this.marginExpandingStyle()}>
        <FieldSet title="Transactions by Credit Card" style={this.getGridStyle()}>
          <Grid
            headers={headers}
            rows={filteredRows}
            onRowSelect={row => this.onRowSelect(row, TransactionStrategy.lastFour)}
          />
        </FieldSet>
        {this.formatFooter(selectedTransactionLastFour, TransactionStrategy.lastFour)}
      </div>
    );
  }

  reset() {
    this.setState({
      selectedTransactionV2: null,
      selectedTransactionV3: null,
      selectedTransactionLastFour: null,
      v2: {},
      v3: {},
      lastFour: {},
    });
  }

  formatFooter(selectedTransaction, version) {
    const isV3 = version === TransactionStrategy.V3;
    // const isV2 = version === TransactionStrategy.V2;
    const isCC = version === TransactionStrategy.lastFour;
    const versionProp = isV3 ? 'v3' : 'v2';
    const versionPropFinal = isCC ? 'Credit Card' : versionProp;
    const { sendNavigationRequest, isTabExistent, isLoading, } = this.props;
    if (!selectedTransaction) {
      return null;
    }

    const cancelACHComponent = isV3 ? null : this.getOptionalCancelAchHTML(selectedTransaction);
    const chargebackComponent = (
      <div className="m-1">
        <ChargebackTransactionForm
          transactionId={selectedTransaction.id}
          externalId={selectedTransaction.externalId}
          version={versionPropFinal}
          serviceOfferingId={selectedTransaction.serviceOfferingId}
        />
      </div>
    );
    const cancelRepresentmentComponent = isV3 ? null : null;
    //  Disabled Until cancel representment until platform works
    //   <div className="m-1">
    //     <CancelRepresentmentForm id={selectedTransaction.id} />
    //   </div>

    const historyNoteComponent = selectedTransaction.paymentHistoryId ? (
      <div className="m-1">
        <HistoryNote paymentHistoryId={selectedTransaction.paymentHistoryId} />
      </div>
    ) : null;

    const isTransactionRefundable = selectedTransaction.transactionType === 'SALE'
      && selectedTransaction.paymentAccountType !== 'CASH';

    const refundComponent = !isTransactionRefundable ? null : (
      <div className="mt-1">
        <RefundForm transactionId={selectedTransaction.id} />
      </div>
    );

    const genResolveComponent = (
      <div className="m-1">
        <GenResolveForm transactionId={selectedTransaction.id} isLoading={isLoading} />
      </div>
    );

    return (
      <div className="container">
        <p>{`Selected ID: ${selectedTransaction.id}`}</p>
        <hr />
        <div className="row">
          <div className="m-1">
            <DetailedTransactionModal
              sendNavigationRequest={sendNavigationRequest}
              transaction={selectedTransaction}
              isTabExistent={isTabExistent}
              version={version}
            />
          </div>
          {refundComponent}
          {cancelACHComponent}
          {chargebackComponent}
          {cancelRepresentmentComponent}
          {historyNoteComponent}
          {genResolveComponent}
        </div>
      </div>
    );
  }

  _setClickEvent(click) {
    this.click = click;
  }

  render() {
    return (
      <div className="container">
        <SearchBar
          comboFields={this.comboFields}
          onClick={data => this.onClick(data)}
          clickConsumer={click => this._setClickEvent(click)}
        />
        {this.getV2Grid()}
        {this.getV3Grid()}
        {this.getLastFourGrid()}
      </div>
    );
  }
}

TransactionSearchView.defaultProps = {
  requestedState: undefined,
};

TransactionSearchView.propTypes = {
  sendNavigationRequest: PropTypes.instanceOf(Function).isRequired,
  isTabExistent: PropTypes.instanceOf(Function).isRequired,
  requestedState: PropTypes.instanceOf(Object),
  isLoading: PropTypes.bool.isRequired,
};

export default TransactionSearchView;
